import { deviceInfo } from './device';
import { SNAPSHOT_METRICS } from './events';
import type { LocalTelemetry } from './store';

/**
 * Адрес приёмника и токен — из окружения сборки, как `local.properties` в Android.
 *
 * **Пустой адрес выключает отправку целиком:** сборка без настроенного приёмника
 * ничего никуда не шлёт и не пытается. Это положение по умолчанию — отправка
 * включается осознанно, а не забывается включённой. Настройка — в `web/README.md`.
 */
const URL_ = import.meta.env.VITE_STATS_URL ?? '';
const TOKEN = import.meta.env.VITE_STATS_TOKEN ?? '';

/** Столько же, сколько на Android: суточный агрегат не горит, а сеть в суде бывает никакой. */
const TIMEOUT_MS = 15_000;

/**
 * Отправка суточных агрегатов на наш сервер.
 *
 * **Очереди с состоянием здесь нет намеренно.** Сервер идемпотентен по
 * `(install_id, date, metric)`: повтор перезаписывает значение, а не складывает.
 * Поэтому отправитель просто шлёт всё, что лежит в хранилище (а лежит не больше
 * 30 суток — TTL), и не помечает отправленное. Такая очередь не умеет
 * рассинхронизироваться с сервером: нечему.
 *
 * Что уходит — ровно то, что юрист видит в настройках: суточные счётчики и срез
 * «сколько дел». Ни названий, ни текстов, ни времени действий.
 */
export async function uploadStats(store: LocalTelemetry, version: string): Promise<boolean> {
  if (!URL_) return false;
  if (!store.isSendingEnabled()) return false;

  const days = store.allDays();
  if (Object.keys(days).length === 0) return false;

  // Прерывание по таймеру, а не `AbortSignal.timeout`: его нет в Safari 15,
  // а версия для iPhone обязана работать на том, что стоит у людей.
  const abort = new AbortController();
  const timer = setTimeout(() => abort.abort(), TIMEOUT_MS);

  try {
    const response = await fetch(URL_, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Stats-Token': TOKEN },
      body: JSON.stringify(payload(store, version, days)),
      signal: abort.signal,
      // Ни печений, ни попутных заголовков: приёмнику нечего о нас знать,
      // а `omit` — единственный способ пообещать это самим кодом.
      credentials: 'omit',
      cache: 'no-store',
    });
    return response.ok;
  } catch {
    // Нет сети, лежит сервер, оборвалось соединение. Следующая попытка — при
    // следующем открытии: терять нечего, данные лежат на устройстве.
    return false;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Тело посылки. Вынесено отдельно и открыто наружу ради теста: единственный способ
 * доказать, что в нём нет ни одной пользовательской строки, — собрать его на настоящих
 * данных и посмотреть. См. `telemetry.test.ts`.
 */
export function payload(
  store: LocalTelemetry,
  version: string,
  days: Record<string, Record<string, number>>,
) {
  return {
    install_id: store.installId(),
    ...deviceInfo(version),
    days: Object.entries(days)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, values]) => {
        // Срез состояния и события лежат в одном хранилище под одинаковыми ключами
        // «дата.имя», а на сервере это разные таблицы: одно — сколько раз сделали,
        // другое — сколько всего есть.
        const metrics: Record<string, number> = {};
        const snapshot: Record<string, number> = {};

        for (const [name, value] of Object.entries(values)) {
          (SNAPSHOT_METRICS.includes(name) ? snapshot : metrics)[name] = value;
        }

        return {
          date,
          ...(Object.keys(metrics).length > 0 ? { metrics } : {}),
          ...(Object.keys(snapshot).length > 0 ? { snapshot } : {}),
        };
      }),
  };
}

/** Настроен ли приёмник в этой сборке. Настройки показывают это юристу честно. */
export const isSendingConfigured = (): boolean => URL_.length > 0;
